import { cva } from "class-variance-authority";
import { CircleAlert, CircleCheck, CircleX, Info } from "lucide-react";
import { HTMLAttributes, ReactNode, Ref } from "react";

import { cn } from "@/lib/cn";

// DESIGN.md → Status: the notice set of one status (wash fill, 200 edge), Slate Grey text with its
// headings and bold words in Ink. That Ink rule has zero specificity, so a link's own colour class
// still wins. Info is the violet one: a message that informs without a status. tabIndex makes it
// focusable when focus is moved to it
const notice = cva(
  "flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-md border p-3 text-sm text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [:where(&)_:where(h2,h3,strong,.font-semibold)]:text-foreground",
  {
    variants: {
      tone: {
        success: "border-success-muted bg-success-wash",
        warning: "border-warning-muted bg-warning-wash",
        error: "border-destructive-muted bg-destructive-wash",
        info: "border-primary-muted bg-primary-wash",
      },
    },
  },
);

// The status as a shape, so it never rests on colour alone
const TONE_ICONS = {
  success: CircleCheck,
  warning: CircleAlert,
  error: CircleX,
  info: Info,
};

interface NoticeProps extends Omit<HTMLAttributes<HTMLElement>, "className" | "children"> {
  tone: keyof typeof TONE_ICONS;
  children: ReactNode;
  /** On the right of the text, e.g. Retry; it wraps below the text when there is no room */
  action?: ReactNode;
  /** `section` for a notice with its own heading, labelled by it */
  as?: "div" | "section";
  /** Places the notice (margin, width, grid position); never changes its look */
  className?: string;
  /** In place of the tone's icon, when the notice has a more specific mark (16px, aria-hidden) */
  icon?: ReactNode;
  ref?: Ref<HTMLElement>;
}

/** A message about the screen or a section of it: its icon and text first, its action on the right. */
export const Notice = ({
  tone,
  children,
  action,
  as: Element = "div",
  className,
  icon,
  ref,
  ...props
}: NoticeProps) => {
  const ToneIcon = TONE_ICONS[tone];
  return (
    <Element
      ref={ref as Ref<HTMLDivElement>}
      className={cn(notice({ tone }), className)}
      {...props}
    >
      <div className="flex min-w-0 flex-1 gap-3">
        {/* mt-0.5 centres the 16px icon on the first 20px line of text. nonScalingStroke keeps
        the stroke at 1.5px, as in SkillPanel; lucide would otherwise scale it down to 1px at 16px */}
        <span className="mt-0.5 flex shrink-0 text-foreground">
          {icon ?? <ToneIcon aria-hidden={true} size={16} strokeWidth={1.5} nonScalingStroke />}
        </span>
        {/* text-pretty keeps the last word of a sentence from wrapping onto a line of its own */}
        <div className="min-w-0 flex-1 wrap-break-word text-pretty">{children}</div>
      </div>
      {action}
    </Element>
  );
};
